import React, { useMemo, useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NS, PROFILE_KEYS } from '../../i18n/constants/keys';
import { translateRole } from '../../i18n/helpers/translateRole';
import { Button } from '../../ui';
import { CustomSelect } from '../../ui/custom_react_select/custom_select';
import { TextInput } from '../../ui/input/text_input/text_input';
import { Modal } from '../../ui/modal/modal';
import { toast } from '../../ui/toast';
import { updateStatus, updateUser } from '../../utils/api_service/user_api';
import { RootState } from '../../utils/redux_store/store';
import { SelectOptions } from '../../utils/types/react_select_types';
import { LiveCameraModal } from './LiveCameraModal/LiveCameraModal';
import {
  PROFILE_SECTION_ICONS,
  ProfileCameraIcon,
  ProfileCheckIcon,
  ProfileEditIcon,
  ProfileLockIcon,
} from './ProfileIcons';
import { TwoFactorSettings } from './TwoFactorSettings';

interface ProfileSection {
  id: string;
  titleKey: string;
  descriptionKey: string;
}

interface NotificationSetting {
  id: string;
  labelKey: string;
  descriptionKey: string;
  enabled: boolean;
}

const ComingSoonBadge: React.FC<{ label: string }> = ({ label }) => (
  <span className='inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-700 whitespace-nowrap'>
    <ProfileLockIcon />
    { label }
  </span>
);

export enum CareerStatus {
  ACTIVELY_LOOKING = 'Actively Looking',
  OPEN_TO_OPPORTUNITIES = 'Open to Opportunities',
  NOT_AVAILABLE = 'Not Available',
}

export const Profile = () => {
  const { t } = useTranslation(NS.profile);
  const { t: tNav } = useTranslation(NS.navbarMenus);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // User Data
  const [firstNameInput, setFirstNameInput] = useState<string>(
    () => localStorage.getItem('firstname') || '',
  );
  const [lastNameInput, setLastNameInput] = useState<string>(
    () => localStorage.getItem('lastname') || '',
  );
  const [emailInput, setEmailInput] = useState<string>(
    () => localStorage.getItem('email') || '',
  );
  const [profileLink, setProfileLink] = useState<string>(
    () => localStorage.getItem('profilePic') || '',
  );
  const [phoneInput, setPhoneInput] = useState<string>(
    () => localStorage.getItem('phone') || '',
  );
  const [jobTitleInput, setJobTitleInput] = useState<string>(
    () => localStorage.getItem('jobTitle') || '',
  );
  const [jobTitleId, setJobTitleId] = useState<string>(
    () => localStorage.getItem('jobTitleId') || '',
  );
  const [bioInput, setBioInput] = useState<string>(
    () => localStorage.getItem('bio') || '',
  );
  // Status of the most recent upload, so a picture still in (or rejected by)
  // face moderation can say so instead of silently reverting to the old one.
  const [profilePicStatus, setProfilePicStatus] = useState<string>(
    () => localStorage.getItem('profilePicStatus') || '',
  );

  // Only employees have a job title (it is a MongoEmployee field).
  const isEmployee = (localStorage.getItem('cls') || '').endsWith('MongoEmployee');
  const jobTitleOptions = useSelector(
    (state: RootState) => state.jobTitles.jobTitlesList,
  );
  // Fall back to the stored label so the current title still shows while the
  // org's job-title list is still loading.
  const selectedJobTitle: SelectOptions | null =
    jobTitleOptions?.find((option) => option.value === jobTitleId)
    || (jobTitleId ? { value: jobTitleId, label: jobTitleInput } : null);

  // UI State
  const [activeSection, setActiveSection] = useState<string>('personal');
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [careerStatus, setCareerStatus] = useState<CareerStatus>(() => {
    const stored = localStorage.getItem('jobStatus');
    return (Object.values(CareerStatus).includes(stored as CareerStatus)
      ? stored
      : CareerStatus.NOT_AVAILABLE) as CareerStatus;
  });

  const handleCareerStatus = async (status: CareerStatus) => {
    try {
      const res = await updateStatus(status);
      if (res.ok && isMounted.current) {
        const response = await res.json();
        setCareerStatus(response.jobSeekingStatus);
        localStorage.setItem('jobStatus', response.jobSeekingStatus);
      }
    } catch (error) {
      console.error('Failed to update career status:', error);
    }
  };

  // Notification Settings
  const [notifications, setNotifications] = useState<NotificationSetting[]>([
    {
      id: 'email_updates',
      labelKey: PROFILE_KEYS.sections.notifications.items.emailUpdates.label,
      descriptionKey:
        PROFILE_KEYS.sections.notifications.items.emailUpdates.description,
      enabled: true,
    },
    {
      id: 'assessment_reminders',
      labelKey:
        PROFILE_KEYS.sections.notifications.items.assessmentReminders.label,
      descriptionKey:
        PROFILE_KEYS.sections.notifications.items.assessmentReminders.description,
      enabled: true,
    },
    {
      id: 'system_notifications',
      labelKey:
        PROFILE_KEYS.sections.notifications.items.systemNotifications.label,
      descriptionKey:
        PROFILE_KEYS.sections.notifications.items.systemNotifications.description,
      enabled: false,
    },
  ]);

  const userRole = localStorage.getItem('role') || 'User';

  const profileSections: ProfileSection[] = useMemo(
    () => [
      {
        id: 'personal',
        titleKey: PROFILE_KEYS.sections.personal.title,
        descriptionKey: PROFILE_KEYS.sections.personal.description,
      },
      {
        id: 'account',
        titleKey: PROFILE_KEYS.sections.account.title,
        descriptionKey: PROFILE_KEYS.sections.account.description,
      },
      {
        id: 'notifications',
        titleKey: PROFILE_KEYS.sections.notifications.title,
        descriptionKey: PROFILE_KEYS.sections.notifications.description,
      },
      {
        id: 'privacy',
        titleKey: PROFILE_KEYS.sections.privacy.title,
        descriptionKey: PROFILE_KEYS.sections.privacy.description,
      },
      ...(userRole === 'Candidate'
        ? [
            {
              id: 'candidate',
              titleKey: PROFILE_KEYS.sections.candidate.title,
              descriptionKey: PROFILE_KEYS.sections.candidate.description,
            },
          ]
        : []),
    ],
    [userRole],
  );

  const careerStatusLabel = (status: CareerStatus): string => {
    const map: Record<CareerStatus, string> = {
      [CareerStatus.ACTIVELY_LOOKING]: t(
        PROFILE_KEYS.sections.candidate.careerStatus.activelyLooking,
      ),
      [CareerStatus.OPEN_TO_OPPORTUNITIES]: t(
        PROFILE_KEYS.sections.candidate.careerStatus.openToOpportunities,
      ),
      [CareerStatus.NOT_AVAILABLE]: t(
        PROFILE_KEYS.sections.candidate.careerStatus.notAvailable,
      ),
    };
    return map[status];
  };


  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await updateUser({
        firstName: firstNameInput,
        lastName: lastNameInput,
        phone: phoneInput,
        bio: bioInput,
        ...(isEmployee ? { jobTitleId } : {}),
      });
      if (!isMounted.current) return;
      if (response?.error || !response?.id) {
        throw new Error(response?.error || 'update failed');
      }

      // Rehydrate from what was actually stored, not from the inputs, so the
      // page never shows a value the server did not accept.
      setFirstNameInput(response.firstName || '');
      setLastNameInput(response.lastName || '');
      setEmailInput(response.email || '');
      setPhoneInput(response.phone || '');
      setBioInput(response.bio || '');
      setJobTitleInput(response.jobTitle || '');
      setJobTitleId(response.jobTitleId || '');
      localStorage.setItem('firstname', response.firstName || '');
      localStorage.setItem('lastname', response.lastName || '');
      localStorage.setItem('email', response.email || '');
      localStorage.setItem('phone', response.phone || '');
      localStorage.setItem('bio', response.bio || '');
      localStorage.setItem('jobTitle', response.jobTitle || '');
      localStorage.setItem('jobTitleId', response.jobTitleId || '');

      window.dispatchEvent(
        new CustomEvent('userProfileUpdated', {
          detail: {
            firstName: response.firstName,
            lastName: response.lastName,
            email: emailInput,
          },
        }),
      );
      window.dispatchEvent(new Event('storage'));

      toast.success(t(PROFILE_KEYS.messages.updateSuccess), {
        position: 'bottom-center',
      });
      setIsEditing(false);
    } catch (error) {
      if (!isMounted.current) return;
      toast.error(t(PROFILE_KEYS.messages.updateFailed), {
        position: 'bottom-center',
      });
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  const handleNotificationToggle = (id: string) => {
    setNotifications((prev) =>
      prev.map((notif) =>
        notif.id === id ? { ...notif, enabled: !notif.enabled } : notif,
      ),
    );
  };

  const userInitials = `${firstNameInput.charAt(0)}${lastNameInput.charAt(
    0,
  )}`.toUpperCase();

  return (
    <div className='min-h-screen bg-slate-50/80'>
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        <div className='mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between'>
          <div>
            <h1 className='text-2xl font-semibold tracking-tight text-slate-900'>
              {t(PROFILE_KEYS.page.title)}
            </h1>
            <p className='mt-1 text-sm text-slate-500'>
              {t(PROFILE_KEYS.page.subtitle)}
            </p>
          </div>
          <span className='inline-flex w-fit items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800'>
            { translateRole(tNav, userRole) }
          </span>
        </div>

        <div className='grid grid-cols-1 gap-6 lg:grid-cols-12'>
          <div className='lg:col-span-4 xl:col-span-3'>
            <nav className='space-y-1 rounded-xl border border-slate-200 bg-white p-2 shadow-sm'>
            { /* Profile Quick Stats */ }
            { /*<div className='bg-white rounded-xl shadow-sm border border-gray-200 p-6'>*/ }
            { /*  <h3 className='font-semibold text-gray-900 mb-4'>Quick Stats</h3>*/ }
            { /*  <div className='space-y-4'>*/ }
            { /*    <div className='flex items-center justify-between'>*/ }
            { /*      <span className='text-sm text-gray-600'>*/ }
            { /*        Profile Completion*/ }
            { /*      </span>*/ }
            { /*      <div className='flex items-center gap-2'>*/ }
            { /*        <div className='w-16 bg-gray-200 rounded-full h-2'>*/ }
            { /*          <div*/ }
            { /*              className='bg-emerald-500 h-2 rounded-full'*/ }
            { /*              style={{ width: '85%' }}*/ }
            { /*          ></div>*/ }
            { /*        </div>*/ }
            { /*        <span className='text-sm font-medium text-gray-900'>*/ }
            { /*          85%*/ }
            { /*        </span>*/ }
            { /*      </div>*/ }
            { /*    </div>*/ }
            { /*    <div className='flex items-center justify-between'>*/ }
            { /*      <span className='text-sm text-gray-600'>Last Login</span>*/ }
            { /*      <span className='text-sm font-medium text-gray-900'>*/ }
            { /*        Today*/ }
            { /*      </span>*/ }
            { /*    </div>*/ }
            { /*    <div className='flex items-center justify-between'>*/ }
            { /*      <span className='text-sm text-gray-600'>Member Since</span>*/ }
            { /*      <span className='text-sm font-medium text-gray-900'>*/ }
            { /*        Jan 2024*/ }
            { /*      </span>*/ }
            { /*    </div>*/ }
            { /*  </div>*/ }
            { /*</div>*/ }

              { profileSections.map((section) => {
                const Icon = PROFILE_SECTION_ICONS[section.id];
                const isActive = activeSection === section.id;
                return (
                  <button
                      key={section.id}
                      type='button'
                      onClick={() => setActiveSection(section.id)}
                      className={`flex w-full items-start gap-3 rounded-lg px-3 py-3 text-left transition-all ${
                        isActive
                          ? 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200'
                          : 'text-slate-700 hover:bg-slate-50'
                      }`}
                  >
                    <span
                        className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                          isActive
                            ? 'bg-emerald-600 text-white'
                            : 'bg-slate-100 text-slate-600'
                        }`}
                    >
                      { Icon ? <Icon className='w-5 h-5' /> : null }
                    </span>
                    <div className='min-w-0'>
                      <div className='text-sm font-semibold'>
                        {t(section.titleKey)}
                      </div>
                      <div className='mt-0.5 text-xs leading-snug text-slate-500'>
                        {t(section.descriptionKey)}
                      </div>
                    </div>
                  </button>
                );
              }) }
            </nav>
          </div>

          <div className='lg:col-span-8 xl:col-span-9'>
            <div className='overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm'>
              { /* Personal Information Section */ }
              { activeSection === 'personal' && (
                <div>
                  { /* Profile Header */ }
                  <div className='bg-gradient-to-r from-emerald-600 to-emerald-700 px-8 py-6 relative overflow-hidden'>
                    <div className='absolute inset-0 bg-gradient-to-r from-white/5 to-transparent'></div>
                    <div className='relative flex items-center gap-6'>
                      <div className='relative group'>
                        <div className='w-24 h-24 rounded-full bg-white/20 backdrop-blur-sm border-4 border-white/30 flex items-center justify-center overflow-hidden'>
                          { profileLink && profileLink !== 'null' ? (
                            <img
                                src={profileLink}
                                alt={t(PROFILE_KEYS.page.profileAlt)}
                                className='w-full h-full object-cover'
                            />
                          ) : (
                            <span className='text-white text-2xl font-bold'>
                              { userInitials }
                            </span>
                          ) }
                        </div>
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className='absolute inset-0 rounded-full bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center'
                        >
                          <ProfileCameraIcon className='w-6 h-6 text-white' />
                        </button>
                      </div>
                      <div className='flex-1'>
                        <h2 className='text-2xl font-bold text-white mb-2'>
                          { firstNameInput } { lastNameInput }
                        </h2>
                        <p className='text-emerald-100 mb-1'>{ emailInput }</p>
                        <p className='text-emerald-200 text-sm'>
                          { jobTitleInput || t(PROFILE_KEYS.fields.jobTitleFallback) }
                        </p>
                        { profilePicStatus === 'Pending' && (
                          <p className='mt-2 inline-flex items-center rounded-lg bg-white/20 px-3 py-1 text-xs font-medium text-white'>
                            { t(PROFILE_KEYS.picture.pending) }
                          </p>
                        ) }
                        { profilePicStatus === 'Not Approved' && (
                          <p className='mt-2 inline-flex items-center rounded-lg bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800'>
                            { t(PROFILE_KEYS.picture.rejected) }
                          </p>
                        ) }
                      </div>
                      <div className='flex gap-3'>
                        { !isEditing ? (
                          <button
                              onClick={() => setIsEditing(true)}
                              className='px-4 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30 transition-colors duration-200 flex items-center gap-2'
                          >
                            <ProfileEditIcon />
                            {t(PROFILE_KEYS.actions.editProfile)}
                          </button>
                        ) : (
                          <div className='flex gap-2'>
                            <button
                                onClick={handleSave}
                                disabled={loading}
                                className='px-4 py-2 bg-white text-emerald-600 rounded-lg hover:bg-emerald-50 transition-colors duration-200 flex items-center gap-2 disabled:opacity-50'
                            >
                              { loading ? (
                                <div className='w-4 h-4 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin'></div>
                              ) : (
                                <ProfileCheckIcon />
                              ) }
                              {t(PROFILE_KEYS.actions.save)}
                            </button>
                            <button
                                onClick={() => setIsEditing(false)}
                                className='px-4 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30 transition-colors duration-200'
                            >
                              {t(PROFILE_KEYS.actions.cancel)}
                            </button>
                          </div>
                        ) }
                      </div>
                    </div>
                  </div>

                  { /* Profile Form */ }
                  <div className='p-8'>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                      <TextInput
                          label={t(PROFILE_KEYS.fields.firstName)}
                          name='firstName'
                          value={firstNameInput}
                          type='text'
                          onChange={(e) => setFirstNameInput(e.target.value)}
                          disabled={!isEditing}
                      />
                      <TextInput
                          label={t(PROFILE_KEYS.fields.lastName)}
                          name='lastName'
                          value={lastNameInput}
                          type='text'
                          onChange={(e) => setLastNameInput(e.target.value)}
                          disabled={!isEditing}
                      />
                      { /* Email is the login identity: changing it needs
                           re-verification, so it is read-only here. */ }
                      <TextInput
                          label={t(PROFILE_KEYS.fields.email)}
                          name='email'
                          value={emailInput}
                          type='email'
                          onChange={() => undefined}
                          disabled
                      />
                      <TextInput
                          label={t(PROFILE_KEYS.fields.phone)}
                          name='phone'
                          value={phoneInput}
                          type='tel'
                          onChange={(e) => setPhoneInput(e.target.value)}
                          disabled={!isEditing}
                      />
                      { isEmployee && (
                        <CustomSelect
                            label={t(PROFILE_KEYS.fields.jobTitle)}
                            name='jobTitle'
                            options={jobTitleOptions}
                            value={selectedJobTitle}
                            clearable
                            onChange={(option: SelectOptions | null) => {
                              setJobTitleId(option?.value || '');
                              setJobTitleInput(option?.label || '');
                            }}
                            disabled={!isEditing}
                        />
                      ) }
                      { /* Department has no home in the data model yet. */ }
                      <div className='relative'>
                        <div className='opacity-40 grayscale pointer-events-none select-none'>
                          <TextInput
                              label={t(PROFILE_KEYS.fields.department)}
                              name='department'
                              value=''
                              type='text'
                              onChange={() => undefined}
                              disabled
                          />
                        </div>
                        <div className='absolute inset-0 flex items-center justify-center'>
                          <ComingSoonBadge label={t(PROFILE_KEYS.actions.comingSoon)} />
                        </div>
                      </div>

                    </div>

                    <div className='mt-6'>
                      <label className='mb-2 block text-sm font-medium text-slate-700'>
                        {t(PROFILE_KEYS.fields.bio)}
                      </label>
                      <textarea
                          value={bioInput}
                          onChange={(e) => setBioInput(e.target.value)}
                          disabled={!isEditing}
                          rows={4}
                          className='w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500 disabled:bg-slate-50 disabled:text-slate-500'
                          placeholder={t(PROFILE_KEYS.fields.bioPlaceholder)}
                      />
                    </div>
                  </div>
                </div>
              ) }

              { /* Account Settings Section */ }
              { activeSection === 'account' && (
                <div className='p-8'>
                  <div className='mb-8'>
                    <h3 className='text-lg font-semibold text-slate-900 mb-2'>
                      {t(PROFILE_KEYS.sections.account.title)}
                    </h3>
                    <p className='text-slate-600'>
                      {t(PROFILE_KEYS.sections.account.intro)}
                    </p>
                  </div>

                  <div className='space-y-6'>

                    { /* Password & Security */ }
                    <div className='bg-gray-50 rounded-lg p-6'>
                      <h4 className='font-medium text-slate-900 mb-4'>
                        {t(PROFILE_KEYS.account.passwordSecurity)}
                      </h4>
                      <div className='space-y-4'>

                        { /* Available */ }
                        <div className='flex items-center justify-between'>
                          <div>
                            <p className='font-medium text-slate-700'>
                              {t(PROFILE_KEYS.account.password)}
                            </p>
                            <p className='text-sm text-slate-500'>
                              {t(PROFILE_KEYS.account.passwordLastUpdated)}
                            </p>
                          </div>
                          <Button
                              variant='primary'
                              label={t(PROFILE_KEYS.actions.changePassword)}
                              onClick={() => (window.location.href = '/change-password')}
                          />
                        </div>

                        { /* Two-factor authentication */ }
                        <TwoFactorSettings />

                      </div>
                    </div>

                    { /* Account Preferences — entire section locked */ }
                    <div className='relative'>
                      <div className='opacity-40 grayscale pointer-events-none select-none'>
                        <div className='bg-gray-50 rounded-lg p-6'>
                          <h4 className='font-medium text-slate-900 mb-4'>
                            {t(PROFILE_KEYS.account.preferences)}
                          </h4>
                          <div className='space-y-4'>
                            <div className='flex items-center justify-between'>
                              <div>
                                <p className='font-medium text-slate-700'>
                                  {t(PROFILE_KEYS.account.language)}
                                </p>
                                <p className='text-sm text-slate-500'>
                                  {t(PROFILE_KEYS.account.languageDesc)}
                                </p>
                              </div>
                              <select tabIndex={-1} className='px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500'>
                                <option>English (US)</option>
                                <option>English (UK)</option>
                                <option>Spanish</option>
                                <option>French</option>
                              </select>
                            </div>
                            <div className='flex items-center justify-between'>
                              <div>
                                <p className='font-medium text-slate-700'>
                                  {t(PROFILE_KEYS.account.timeZone)}
                                </p>
                                <p className='text-sm text-slate-500'>
                                  {t(PROFILE_KEYS.account.timeZoneDesc)}
                                </p>
                              </div>
                              <select tabIndex={-1} className='px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500'>
                                <option>UTC-8 (Pacific Time)</option>
                                <option>UTC-5 (Eastern Time)</option>
                                <option>UTC+0 (GMT)</option>
                                <option>UTC+1 (Central European Time)</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className='absolute inset-0 flex items-center justify-center'>
                        <ComingSoonBadge label={t(PROFILE_KEYS.actions.comingSoon)} />
                      </div>
                    </div>

                    { /* Recent Activity */ }
                    { /*<div className='bg-gray-50 rounded-lg p-6'>*/ }
                    { /*  <h4 className='font-medium text-gray-900 mb-4'>Recent Activity</h4>*/ }
                    { /*  <div className='space-y-3'>*/ }
                    { /*    <div className='flex items-center gap-3 p-3 bg-white rounded-lg'>*/ }
                    { /*      <div className='w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center'>*/ }
                    { /*        <svg className='w-4 h-4 text-blue-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>*/ }
                    { /*          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' />*/ }
                    { /*        </svg>*/ }
                    { /*      </div>*/ }
                    { /*      <div className='flex-1'>*/ }
                    { /*        <p className='text-sm font-medium text-gray-900'>Profile updated</p>*/ }
                    { /*        <p className='text-xs text-gray-500'>2 hours ago</p>*/ }
                    { /*      </div>*/ }
                    { /*    </div>*/ }
                    { /*    <div className='flex items-center gap-3 p-3 bg-white rounded-lg'>*/ }
                    { /*      <div className='w-8 h-8 bg-green-100 rounded-full flex items-center justify-center'>*/ }
                    { /*        <svg className='w-4 h-4 text-green-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>*/ }
                    { /*          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />*/ }
                    { /*        </svg>*/ }
                    { /*      </div>*/ }
                    { /*      <div className='flex-1'>*/ }
                    { /*        <p className='text-sm font-medium text-gray-900'>Assessment completed</p>*/ }
                    { /*        <p className='text-xs text-gray-500'>1 day ago</p>*/ }
                    { /*      </div>*/ }
                    { /*    </div>*/ }
                    { /*    <div className='flex items-center gap-3 p-3 bg-white rounded-lg'>*/ }
                    { /*      <div className='w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center'>*/ }
                    { /*        <svg className='w-4 h-4 text-purple-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>*/ }
                    { /*          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z' />*/ }
                    { /*        </svg>*/ }
                    { /*      </div>*/ }
                    { /*      <div className='flex-1'>*/ }
                    { /*        <p className='text-sm font-medium text-gray-900'>Password changed</p>*/ }
                    { /*        <p className='text-xs text-gray-500'>3 days ago</p>*/ }
                    { /*      </div>*/ }
                    { /*    </div>*/ }
                    { /*  </div>*/ }
                    { /*</div>*/ }

                  </div>
                </div>
              ) }

              { /* Notifications Section */ }
              { activeSection === 'notifications' && (
                <div className='p-8'>
                  <div className='mb-8'>
                    <h3 className='text-lg font-semibold text-slate-900 mb-2'>
                      {t(PROFILE_KEYS.sections.notifications.heading)}
                    </h3>
                    <p className='text-slate-600'>
                      {t(PROFILE_KEYS.sections.notifications.intro)}
                    </p>
                  </div>

                  <div className='relative'>
                    <div className='space-y-4 opacity-40 grayscale pointer-events-none select-none'>
                      { notifications.map((notification) => (
                        <div
                            key={notification.id}
                            className='flex items-center justify-between p-4 border border-gray-200 rounded-lg'
                        >
                          <div>
                            <h4 className='font-medium text-slate-900'>
                              {t(notification.labelKey)}
                            </h4>
                            <p className='text-sm text-slate-500'>
                              {t(notification.descriptionKey)}
                            </p>
                          </div>
                          <button
                              tabIndex={-1}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                notification.enabled ? 'bg-emerald-600' : 'bg-gray-200'
                              }`}
                          >
                            <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  notification.enabled ? 'translate-x-6' : 'translate-x-1'
                                }`}
                            />
                          </button>
                        </div>
                      )) }
                    </div>

                    <div className='absolute inset-0 flex items-center justify-center'>
                      <ComingSoonBadge label={t(PROFILE_KEYS.actions.comingSoon)} />
                    </div>
                  </div>
                </div>
              ) }

              { /* Privacy & Security Section */ }
              { activeSection === 'privacy' && (
                <div className='p-8'>
                  <div className='mb-8'>
                    <h3 className='text-lg font-semibold text-slate-900 mb-2'>
                      {t(PROFILE_KEYS.sections.privacy.title)}
                    </h3>
                    <p className='text-slate-600'>
                      {t(PROFILE_KEYS.sections.privacy.intro)}
                    </p>
                  </div>

                  <div className='space-y-6'>
                    <div className='bg-blue-50 border border-blue-200 rounded-lg p-6'>
                      <div className='flex items-start gap-3'>
                        <div className='w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center'>
                          <svg
                              className='w-4 h-4 text-blue-600'
                              fill='none'
                              stroke='currentColor'
                              viewBox='0 0 24 24'
                          >
                            <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                            />
                          </svg>
                        </div>
                        <div>
                          <h4 className='font-medium text-blue-900 mb-2'>
                            {t(PROFILE_KEYS.sections.privacy.policyTitle)}
                          </h4>
                          <p className='text-blue-700 text-sm mb-4'>
                            {t(PROFILE_KEYS.sections.privacy.policyDescription)}
                          </p>
                          <a
                              href='https://www.hiacuity.com/privacy-policy'
                              target='_blank'
                              rel='noopener noreferrer'
                              className='inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm'
                          >
                            {t(PROFILE_KEYS.sections.privacy.readPolicy)}
                            <svg
                                className='w-4 h-4'
                                fill='none'
                                stroke='currentColor'
                                viewBox='0 0 24 24'
                            >
                              <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  strokeWidth={2}
                                  d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14'
                              />
                            </svg>
                          </a>
                        </div>
                      </div>
                    </div>

                    <div className='relative'>
                      <div className='bg-gray-50 rounded-lg p-6 opacity-40 grayscale pointer-events-none select-none'>
                        <h4 className='font-medium text-slate-900 mb-4'>
                          {t(PROFILE_KEYS.sections.privacy.dataManagement)}
                        </h4>
                        <div className='space-y-4'>
                          <div className='flex items-center justify-between'>
                            <div>
                              <p className='font-medium text-slate-700'>
                                {t(PROFILE_KEYS.sections.privacy.downloadData)}
                              </p>
                              <p className='text-sm text-slate-500'>
                                {t(PROFILE_KEYS.sections.privacy.downloadDataDesc)}
                              </p>
                            </div>
                            <Button
                                variant='primary'
                                label={t(PROFILE_KEYS.sections.privacy.requestDownload)}
                                onClick={() =>
                                  toast.info(t(PROFILE_KEYS.messages.dataDownloadRequested))
                                }
                            />
                          </div>
                          <div className='flex items-center justify-between'>
                            <div>
                              <p className='font-medium text-slate-700'>
                                {t(PROFILE_KEYS.sections.privacy.deleteAccount)}
                              </p>
                              <p className='text-sm text-slate-500'>
                                {t(PROFILE_KEYS.sections.privacy.deleteAccountDesc)}
                              </p>
                            </div>
                            <button tabIndex={-1}
                                className='px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors'>
                              {t(PROFILE_KEYS.sections.privacy.deleteAccount)}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className='absolute inset-0 flex items-center justify-center'>
                        <ComingSoonBadge label={t(PROFILE_KEYS.actions.comingSoon)} />
                      </div>
                    </div>
                  </div>
                </div>
              ) }
              { activeSection === 'candidate' && userRole === 'Candidate' && (
                <div className='p-8'>
                  <div className='mb-8'>
                    <h3 className='text-lg font-semibold text-slate-900 mb-2'>
                      {t(PROFILE_KEYS.sections.candidate.title)}
                    </h3>
                    <p className='text-slate-600'>
                      {t(PROFILE_KEYS.sections.candidate.intro)}
                    </p>
                  </div>

                  <div className='space-y-6'>

                    { /* Career Status */ }
                    <div className='bg-gray-50 rounded-lg p-6'>
                      <h4 className='font-medium text-slate-900 mb-4'>
                        {t(PROFILE_KEYS.sections.candidate.jobSeekingStatus)}
                      </h4>
                      <div className='relative'>
                        <select
                            value={careerStatus}
                            onChange={(e) => handleCareerStatus(e.target.value as CareerStatus)}
                            className='w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg appearance-none
              focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500
              disabled:bg-gray-50 disabled:text-gray-500 bg-white'
                        >
                          { Object.values(CareerStatus).map((status) => (
                            <option key={status} value={status}>
                              { careerStatusLabel(status) }
                            </option>
                          )) }
                        </select>
                        <div className='absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none'>
                          <svg className='w-4 h-4 text-gray-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
                          </svg>
                        </div>
                      </div>
                      <div className='mt-2'>
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium
            ${careerStatus === CareerStatus.ACTIVELY_LOOKING
                          ? 'bg-emerald-100 text-emerald-800'
                          : careerStatus === CareerStatus.OPEN_TO_OPPORTUNITIES
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-600'
          }`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full
              ${careerStatus === CareerStatus.ACTIVELY_LOOKING
                            ? 'bg-emerald-500'
                            : careerStatus === CareerStatus.OPEN_TO_OPPORTUNITIES
                              ? 'bg-blue-500'
                              : 'bg-gray-400'
            }`}
                          />
                          { careerStatusLabel(careerStatus) }
                        </span>
                      </div>
                    </div>

                    { /* Resume Management */ }
                    <div className='bg-gray-50 rounded-lg p-6'>
                      <h4 className='font-medium text-slate-900 mb-1'>
                        {t(PROFILE_KEYS.sections.candidate.resume)}
                      </h4>
                      <p className='text-sm text-slate-500 mb-4'>
                        {t(PROFILE_KEYS.sections.candidate.resumeHint)}
                      </p>

                      { /* Upload Area */ }
                      <label className='flex flex-col items-center justify-center w-full h-36 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-emerald-400 hover:bg-emerald-50 transition-colors duration-200 bg-white mb-4'>
                        <div className='flex flex-col items-center justify-center gap-2'>
                          <svg className='w-8 h-8 text-gray-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2}
                                d='M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12' />
                          </svg>
                          <p className='text-sm text-slate-600'>
                            <span className='font-medium text-emerald-600'>
                              {t(PROFILE_KEYS.sections.candidate.uploadClick)}
                            </span>{' '}
                            {t(PROFILE_KEYS.sections.candidate.uploadOrDrag)}
                          </p>
                          <p className='text-xs text-slate-400'>
                            {t(PROFILE_KEYS.sections.candidate.uploadFormats)}
                          </p>
                        </div>
                        <input type='file' className='hidden' accept='.pdf,.doc,.docx' />
                      </label>

                      { /* Current Resume Card */ }
                      <div className='flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg'>
                        <div className='flex items-center gap-3'>
                          <div className='w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0'>
                            <svg className='w-5 h-5 text-red-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2}
                                  d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' />
                            </svg>
                          </div>
                          <div>
                            <p className='text-sm font-medium text-slate-900'>
                              {t(PROFILE_KEYS.sections.candidate.resumeFileName)}
                            </p>
                            <p className='text-xs text-slate-500'>
                              {t(PROFILE_KEYS.sections.candidate.resumeMeta)}
                            </p>
                          </div>
                        </div>
                        <div className='flex items-center gap-2'>
                          { /* View */ }
                          <button
                              onClick={() =>
                                toast.info(t(PROFILE_KEYS.messages.resumePreview))
                              }
                              className='p-2 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors'
                              title={t(PROFILE_KEYS.sections.candidate.viewResume)}
                          >
                            <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2}
                                  d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
                              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2}
                                  d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' />
                            </svg>
                          </button>
                          { /* Download */ }
                          <button
                              onClick={() =>
                                toast.info(t(PROFILE_KEYS.messages.resumeDownloading))
                              }
                              className='p-2 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors'
                              title={t(PROFILE_KEYS.sections.candidate.downloadResume)}
                          >
                            <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2}
                                  d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4' />
                            </svg>
                          </button>
                          { /* Delete */ }
                          <button
                              onClick={() =>
                                toast.warn(t(PROFILE_KEYS.messages.resumeDeleted))
                              }
                              className='p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors'
                              title={t(PROFILE_KEYS.sections.candidate.deleteResume)}
                          >
                            <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2}
                                  d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              ) }
            </div>
          </div>
        </div>
      </div>

      { /* Profile Picture Modal */ }
      { isModalOpen && (
        <LiveCameraModal
            isOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            setProfileLink={setProfileLink}
            currentProfilePic={profileLink}
            userName={`${firstNameInput} ${lastNameInput}`}
            userRole={userRole}
        />
      ) }
    </div>
  );
};
